import { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useProject, useUpdateProject } from '../../../api/projects';
import { useReferenceLists } from '../../../api/referenceLists';
import { PageMeta } from '../shared/PageMeta';

interface FormValues {
  title: string;
  description: string;
  adaptationZone: string;
  deliveryAddress: string;
  deliveryCountry: string;
  neededBy: string;
  links: string;
}

export function EditProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const projectId = parseInt(id || '0', 10);
  const { data: project, isLoading } = useProject(projectId);
  const updateProject = useUpdateProject();
  const { data: refLists } = useReferenceLists();

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>();

  useEffect(() => {
    if (project) {
      reset({
        title: project.title,
        description: project.description,
        adaptationZone: project.adaptationZone || '',
        deliveryAddress: project.deliveryAddress,
        deliveryCountry: project.deliveryCountry,
        neededBy: project.neededBy ? project.neededBy.split('T')[0] : '',
        links: project.links.join('\n'),
      });
    }
  }, [project, reset]);

  const onSubmit = async (data: FormValues) => {
    try {
      const links = data.links ? data.links.split('\n').map((l) => l.trim()).filter(Boolean) : [];
      await updateProject.mutateAsync({
        id: projectId,
        data: {
          title: data.title,
          description: data.description,
          adaptationZone: data.adaptationZone || undefined,
          deliveryAddress: data.deliveryAddress,
          deliveryCountry: data.deliveryCountry,
          neededBy: data.neededBy || undefined,
          links,
        },
      });
      toast.success('Project updated');
      navigate(`/marketplace/projects/${projectId}`);
    } catch (err: unknown) {
      toast.error((err as Error).message || 'Failed to update project');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
      </div>
    );
  }

  if (!project) {
    return <div className="container mx-auto px-4 py-16 text-center"><h2 className="text-2xl">Project Not Found</h2></div>;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-2xl">
      <PageMeta title={`Edit: ${project.title}`} description="Edit your project" path={`/marketplace/projects/${projectId}/edit`} />

      <Link to={`/marketplace/projects/${projectId}`} className="text-sm text-spi-green hover:underline mb-4 inline-block">&larr; Back to project</Link>
      <h2 className="text-3xl mb-6">Edit Project</h2>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-8 space-y-4">
        <div>
          <label className="input-label">Title *</label>
          <input {...register('title', { required: 'Title is required', maxLength: 200 })} className="input" />
          {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
        </div>

        <div>
          <label className="input-label">Description *</label>
          <textarea {...register('description', { required: 'Description is required' })} className="input min-h-[120px]" />
          {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description.message}</p>}
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="input-label">Delivery Address *</label>
            <input {...register('deliveryAddress', { required: 'Address is required' })} className="input" />
          </div>
          <div>
            <label className="input-label">Delivery Country *</label>
            <input {...register('deliveryCountry', { required: 'Country is required', minLength: 2 })} className="input" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="input-label">USDA Adaptation Zone</label>
            <select {...register('adaptationZone')} className="input">
              <option value="">None</option>
              {refLists?.zone?.map((item) => (
                <option key={item.id} value={item.value}>Zone {item.value}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="input-label">Needed By</label>
            <input type="date" {...register('neededBy')} className="input" />
          </div>
        </div>

        <div>
          <label className="input-label">Links (one per line)</label>
          <textarea {...register('links')} className="input min-h-[80px]" />
        </div>

        <div className="flex gap-3 pt-2">
          <Link to={`/marketplace/projects/${projectId}`} className="btn btn-outline flex-1 text-center">
            Cancel
          </Link>
          <button type="submit" disabled={updateProject.isPending} className="btn btn-primary flex-1">
            {updateProject.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
}
